import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './ServicesPage.css';
import { setPageTitle, pageTitles, pageDescriptions } from '../utils/pageTitle';
import { useLanguage } from '../contexts/LanguageContext';
import { initializeSEOEnhancements } from '../utils/seoEnhancements';
import {
  DevOpsIcon,
  CloudIcon,
  ManagedServicesIcon,
  AISolutions,
  MobileDevIcon,
  QualityAssuranceIcon
} from '../components/icons/ServiceIcons';

const ServicesPage = () => {
  const { t, localizedPath, language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    setPageTitle(pageTitles.services, pageDescriptions.services, 'services', language);
    
    // Initialize SEO enhancements for services page
    initializeSEOEnhancements('services', {
      serviceName: 'Comprehensive IT Services',
      description: pageDescriptions.services,
      title: pageTitles.services,
      url: 'https://radixtechs.com/services'
    });
  }, [language]);

  // Handle scrolling to specific service section when hash is present
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1); // Remove the # symbol
      const element = document.getElementById(elementId);
      if (element) {
        // Add a small delay to ensure the page has rendered
        setTimeout(() => {
          const headerOffset = 80; // Account for fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location.hash]);

  const services = [
    {
      id: 'ai-solutions',
      icon: <AISolutions size={64} />,
      title: t('services.aiSolutions.title'),
      description: t('services.aiSolutions.description'),
      features: t('services.aiSolutions.features') || [],
      technologies: t('services.aiSolutions.technologies') || []
    },
    {
      id: 'cloud-migration',
      icon: <CloudIcon size={64} />,
      title: t('services.cloudDeployment.title'),
      description: t('services.cloudDeployment.description'),
      features: t('services.cloudDeployment.features') || [],
      technologies: t('services.cloudDeployment.technologies') || []
    },
    {
      id: 'mobile-development',
      icon: <MobileDevIcon size={64} />,
      title: t('services.mobileDevelopment.title'),
      description: t('services.mobileDevelopment.description'),
      features: t('services.mobileDevelopment.features') || [],
      technologies: t('services.mobileDevelopment.technologies') || []
    },
    {
      id: 'devops-automation',
      icon: <DevOpsIcon size={64} />,
      title: t('services.devOps.title'),
      description: t('services.devOps.description'),
      features: t('services.devOps.features') || [],
      technologies: t('services.devOps.technologies') || []
    },
    {
      id: 'managed-services',
      icon: <ManagedServicesIcon size={64} />,
      title: t('services.managedServices.title'),
      description: t('services.managedServices.description'),
      features: t('services.managedServices.features') || [],
      technologies: t('services.managedServices.technologies') || []
    },
    {
      id: 'quality-assurance',
      icon: <QualityAssuranceIcon size={64} />,
      title: t('services.qualityAssurance.title'),
      description: t('services.qualityAssurance.description'),
      features: t('services.qualityAssurance.features') || [],
      technologies: t('services.qualityAssurance.technologies') || []
    }
  ];

  const process = [
    {
      step: t('services.process.discovery.step'),
      title: t('services.process.discovery.title'),
      description: t('services.process.discovery.description')
    },
    {
      step: t('services.process.planning.step'),
      title: t('services.process.planning.title'),
      description: t('services.process.planning.description')
    },
    {
      step: t('services.process.development.step'),
      title: t('services.process.development.title'),
      description: t('services.process.development.description')
    },
    {
      step: t('services.process.delivery.step'),
      title: t('services.process.delivery.title'),
      description: t('services.process.delivery.description')
    },
    {
      step: t('services.process.support.step'),
      title: t('services.process.support.title'),
      description: t('services.process.support.description')
    }
  ];

  return (
    <div className="services-page">
      <section className="services-hero section">
        <div className="container">
          <div className="services-hero-content">
            <h1 className="page-title">{t('services.pageTitle')}</h1>
            <p className="services-hero-description">
              {t('services.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      <section className="services-grid-section section">
        <div className="container">
          <div className="services-detailed-grid">
            {services.map((service, index) => (
              <div key={index} id={service.id} className="service-detailed-card">
                <div className="service-header">
                  <div className="service-icon-large">{service.icon}</div>
                  <h3 className="service-title-large">{service.title}</h3>
                  <p className="service-description-large">{service.description}</p>
                </div>

                <div className="service-features">
                  <h4 className="features-title">{t('services.whatsIncluded')}</h4>
                  <ul className="features-list">
                    {Array.isArray(service.features) && service.features.map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <span className="feature-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-technologies">
                  <h4 className="tech-title">{t('services.technologies')}</h4>
                  <div className="tech-tags">
                    {Array.isArray(service.technologies) && service.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>

                <a href={localizedPath('/contact')} className="service-cta">
                  {t('services.getStarted')}
                  <span className="cta-arrow">→</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section section">
        <div className="container">
          <h2 className="section-title">{t('services.processTitle')}</h2>
          <div className="process-timeline">
            {process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
